import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/auth/useAuth';
import { startLinkInPage } from '@/lib/auth-provider-connect';
import { showProviderConnectionError } from '@/lib/auth-provider-error';
import { takeAuthLinkOutcome } from '@/lib/auth-link-return';

/**
 * Report an account link that ran in the main tab instead of a popup (the
 * popup was blocked). The popup flow toasts from its caller; a full-page link
 * reloads the app, so its outcome arrives through `captureAuthLinkOutcome`
 * and is drained here once, on mount.
 *
 * Retry runs the link in the page again: the popup that was blocked the
 * first time would most likely be blocked again.
 *
 * Mounted from `AuthedShell` next to `useFirstSignInToast`.
 */
export function useAuthLinkOutcomeToast(): void {
  const { providers } = useAuth();
  // Read at toast time: `/auth/me` may still be loading on the first render.
  const providersRef = useRef(providers);
  useEffect(() => {
    providersRef.current = providers;
  });

  useEffect(() => {
    // Deferred one task: the root `<Toaster />` mounts after this shell in the
    // same commit, and sonner drops toasts published before it subscribes.
    // Draining inside the timer keeps StrictMode's mount-unmount-mount from
    // consuming the outcome in a run whose timer is then cancelled.
    const timer = setTimeout(() => {
      const outcome = takeAuthLinkOutcome();
      if (!outcome) return;
      const providerLabel = providersRef.current[outcome.provider]?.label;
      const report = (error: string | undefined) => {
        if (error === undefined) return;
        showProviderConnectionError({
          provider: outcome.provider,
          providerLabel,
          error,
          onRetry: () =>
            void startLinkInPage(outcome.provider, outcome.linkStartPath).then((result) =>
              report(result?.error),
            ),
        });
      };
      if (outcome.error) {
        report(outcome.error);
      } else {
        toast.success(`${providerLabel ?? outcome.provider} connected`);
      }
    }, 0);
    return () => clearTimeout(timer);
  }, []);
}
